import { renderHook, waitFor } from '@testing-library/react';
import { useAutocompleteUtils } from '../../utils/autocompleteUtils';

// Mock the API
jest.mock('../../API', () => ({
  getCountriesStates: jest.fn()
}));

import Api from '../../API';
const mockApi = Api as jest.Mocked<typeof Api>;

describe('useAutocompleteUtils', () => {
  beforeEach(() => {
    jest.clearAllMocks();
  });

  const mockCountriesStatesData = {
    res: {
      countries: [
        {
          id: 1,
          name: 'Australia',
          iso: 'AU',
          iso3: 'AUS',
          phone_prefix: '+61',
          printable_name: 'Australia'
        },
        {
          id: 2,
          name: 'United States',
          iso: 'US',
          iso3: 'USA',
          phone_prefix: '+1',
          printable_name: 'United States'
        }
      ],
      states: [
        {
          id: 1,
          name: 'NSW',
          country_id: 1
        },
        {
          id: 2,
          name: 'VIC',
          country_id: 1
        },
        {
          id: 3,
          name: 'California',
          country_id: 2
        },
        {
          id: 4,
          name: 'New York',
          country_id: 2
        }
      ]
    }
  };

  it('should initialize with empty arrays', () => {
    mockApi.getCountriesStates.mockResolvedValue(mockCountriesStatesData);

    const { result } = renderHook(() => useAutocompleteUtils());

    expect(result.current.countries).toEqual([]);
    expect(result.current.states).toEqual([]);
    expect(result.current.isLoading).toBe(true);
  });

  it('should load countries and states data', async () => {
    mockApi.getCountriesStates.mockResolvedValue(mockCountriesStatesData);

    const { result } = renderHook(() => useAutocompleteUtils());

    await new Promise((resolve) => setTimeout(resolve, 0));

    expect(result.current.countries).toEqual([
      {
        id: 1,
        name: 'Australia',
        iso: 'AU',
        iso3: 'AUS',
        phone_prefix: '+61',
        printable_name: 'Australia'
      },
      {
        id: 2,
        name: 'United States',
        iso: 'US',
        iso3: 'USA',
        phone_prefix: '+1',
        printable_name: 'United States'
      }
    ]);
    expect(result.current.states).toEqual([
      { id: 1, name: 'NSW', country_id: 1 },
      { id: 2, name: 'VIC', country_id: 1 },
      { id: 3, name: 'California', country_id: 2 },
      { id: 4, name: 'New York', country_id: 2 }
    ]);
    expect(result.current.isLoading).toBe(false);
  });

  it('should handle API errors', async () => {
    const consoleError = jest.spyOn(console, 'error').mockImplementation(() => undefined);
    mockApi.getCountriesStates.mockRejectedValue(new Error('API Error'));

    const { result } = renderHook(() => useAutocompleteUtils());

    await waitFor(() => {
      expect(result.current.isLoading).toBe(false);
    });

    expect(result.current.countries).toEqual([]);
    expect(result.current.states).toEqual([]);
    expect(consoleError).toHaveBeenCalledWith(
      'Error fetching countries and states:',
      expect.any(Error)
    );

    consoleError.mockRestore();
  });

  it('should handle empty API response', async () => {
    mockApi.getCountriesStates.mockResolvedValue({ res: { countries: [], states: [] } });

    const { result } = renderHook(() => useAutocompleteUtils());

    await waitFor(() => {
      expect(result.current.isLoading).toBe(false);
    });

    expect(result.current.countries).toEqual([]);
    expect(result.current.states).toEqual([]);
  });

  it('should handle malformed API response', async () => {
    mockApi.getCountriesStates.mockResolvedValue({ res: {} } as any);

    const { result } = renderHook(() => useAutocompleteUtils());

    await waitFor(() => {
      expect(result.current.isLoading).toBe(false);
    });

    expect(result.current.countries).toEqual([]);
    expect(result.current.states).toEqual([]);
  });

  it('should only call API once per hook instance', async () => {
    mockApi.getCountriesStates.mockResolvedValue(mockCountriesStatesData);

    const { rerender } = renderHook(() => useAutocompleteUtils());

    await new Promise((resolve) => setTimeout(resolve, 0));

    expect(mockApi.getCountriesStates).toHaveBeenCalledTimes(1);

    // Rerender should not call API again
    rerender();
    expect(mockApi.getCountriesStates).toHaveBeenCalledTimes(1);
  });

  it('should handle different country and state structures', async () => {
    const customData = {
      res: {
        countries: [
          {
            id: 1,
            name: 'Canada',
            iso: 'CA'
            // Missing some optional fields
          }
        ],
        states: [
          {
            id: 1,
            name: 'Ontario',
            country_id: 1,
            // Additional field
            code: 'ON'
          }
        ]
      }
    };

    mockApi.getCountriesStates.mockResolvedValue(customData);

    const { result } = renderHook(() => useAutocompleteUtils());

    await waitFor(() => {
      expect(result.current.isLoading).toBe(false);
    });

    expect(result.current.countries).toEqual([
      {
        id: 1,
        name: 'Canada',
        iso: 'CA'
      }
    ]);
    expect(result.current.states).toEqual([
      {
        id: 1,
        name: 'Ontario',
        country_id: 1,
        code: 'ON'
      }
    ]);
  });
});
